import React, { useState } from "react";
import { appleApi } from "../../services/api";
import CountryList from "../CountryList";
import { FaImages, FaSearch, FaTimes, FaMobileAlt, FaTabletAlt, FaTv } from "react-icons/fa";

const CompetitorScreenshots = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("us");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError("Please enter a keyword");
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const response = await appleApi.get("apple-apps-screenshots/", {
        params: {
          keyword: keyword,
          country: country || "us",
        },
      });
      
      if (response.data && Array.isArray(response.data)) {
        setResults(response.data);
      } else {
        setResults([]);
        setError("No results found or unexpected response format.");
      }
    } catch (err) {
      console.error("Error fetching screenshots:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (imageUrl) => {
    setModalImage(imageUrl);
  };

  const closeModal = () => {
    setModalImage(null);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 bg-blue-600 rounded-lg shadow-lg">
          <FaImages className="text-white text-2xl" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Competitor Screenshots</h1>
      </div>

      {/* Input Section */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Keyword</label>
            <div className="relative">
              <input
                type="text"
                className="w-full bg-gray-50 border border-gray-200 rounded-lg py-3 pr-4 pl-12 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter keyword (e.g., photo editor)..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            </div>
          </div>

          <div className="md:col-span-5">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
            <CountryList onSelect={(code) => setCountry(code.toLowerCase())} initialCountryCode={country.toUpperCase()} />
          </div>

          <div className="md:col-span-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <FaSearch className="mr-2" />
                  Search
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {results.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/6">App Details</th>
                  <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-24">Icon</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Screenshots</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {results.map((app) => (
                  <tr key={app.trackId} className="hover:bg-gray-50 transition-colors">
                    {/* App Title Column */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <a 
                          href={app.trackViewUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline mb-1"
                        >
                          {app.title}
                        </a>
                        <span className="text-xs text-gray-500">ID: {app.trackId}</span>
                        {app.bundleId && <span className="text-xs text-gray-400 break-all">{app.bundleId}</span>}
                      </div>
                    </td>

                    {/* Icon Column */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex justify-center">
                        <img 
                          src={app.icon} 
                          alt={app.title} 
                          className="h-16 w-16 rounded-xl shadow-sm object-cover border border-gray-100"
                        />
                      </div>
                    </td>

                    {/* Screenshots Column */}
                    <td className="px-6 py-4">
                      <div className="space-y-4">
                        {/* iPhone Screenshots */}
                        {app.screenshots?.iphone && app.screenshots.iphone.length > 0 && (
                          <div>
                            <div className="flex items-center space-x-2 mb-2 text-xs font-semibold text-gray-500 uppercase">
                              <FaMobileAlt className="text-gray-400" />
                              <span>iPhone</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {app.screenshots.iphone.map((url, idx) => (
                                <div 
                                  key={`iphone-${idx}`} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openModal(url)}
                                >
                                  <img 
                                    src={url} 
                                    alt={`iPhone Screenshot ${idx + 1}`} 
                                    className="h-24 w-auto rounded border border-gray-200 shadow-sm hover:shadow-md transition-shadow object-contain bg-white"
                                    style={{ minWidth: '40px', minHeight: '60px' }}
                                  />
                                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity rounded pointer-events-none" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* iPad Screenshots */}
                        {app.screenshots?.ipad && app.screenshots.ipad.length > 0 && (
                          <div>
                             <div className="flex items-center space-x-2 mb-2 text-xs font-semibold text-gray-500 uppercase mt-3">
                              <FaTabletAlt className="text-gray-400" />
                              <span>iPad</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {app.screenshots.ipad.map((url, idx) => (
                                <div 
                                  key={`ipad-${idx}`} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openModal(url)}
                                >
                                  <img 
                                    src={url} 
                                    alt={`iPad Screenshot ${idx + 1}`} 
                                    className="h-24 w-auto rounded border border-gray-200 shadow-sm hover:shadow-md transition-shadow object-contain bg-white"
                                    style={{ minWidth: '40px', minHeight: '60px' }}
                                  />
                                   <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity rounded pointer-events-none" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Apple TV Screenshots */}
                        {app.screenshots?.appletv && app.screenshots.appletv.length > 0 && (
                          <div>
                             <div className="flex items-center space-x-2 mb-2 text-xs font-semibold text-gray-500 uppercase mt-3">
                              <FaTv className="text-gray-400" />
                              <span>Apple TV</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {app.screenshots.appletv.map((url, idx) => (
                                <div 
                                  key={`tv-${idx}`} 
                                  className="relative group cursor-pointer"
                                  onClick={() => openModal(url)}
                                >
                                  <img 
                                    src={url} 
                                    alt={`AppleTV Screenshot ${idx + 1}`} 
                                    className="h-24 w-auto rounded border border-gray-200 shadow-sm hover:shadow-md transition-shadow object-contain bg-white"
                                    style={{ minWidth: '40px', minHeight: '60px' }}
                                  />
                                   <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity rounded pointer-events-none" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {(!app.screenshots?.iphone?.length && !app.screenshots?.ipad?.length && !app.screenshots?.appletv?.length) && (
                           <p className="text-sm text-gray-400 italic">No screenshots available</p>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-md"
          onClick={closeModal}
        >
          <div className="relative max-w-7xl max-h-screen p-2" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeModal}
              className="absolute -top-12 right-0 md:-right-12 bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-full shadow-lg transition-colors z-50 group"
              title="Close"
            >
              <FaTimes size={20} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
            <img 
              src={modalImage} 
              alt="Full view" 
              className="max-h-[85vh] max-w-full rounded-lg shadow-2xl bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitorScreenshots;
